import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './client';
import type { Paginated } from './types';

export type ReviewMatch = {
  id: number;
  store: string;
  product_name: string;
  size: string;
  regular_price: string | null;
  promo_price: string | null;
  confidence: number;
  pinned: boolean;
  /** The one to buy. Alternates are kept so a sale on any is visible. */
  chosen: boolean;
  /** Where to see it on the store's site. Blank where the shape is unknown. */
  product_url: string;
  image_url: string;
  /** The broadened term that actually found it, when it was not the name. */
  matched_as: string;
};

export type ReviewIngredient = {
  id: number;
  ingredient_id: number;
  ingredient_name: string;
  /** As printed on the card. The API divides to per-serving on the way in. */
  total_quantity: string | null;
  quantity_per_serving: string | null;
  unit: string;
  raw_text: string;
  optional: boolean;
  position: number;
  verified: boolean;
  matches: ReviewMatch[];
};

export type ReviewRecipe = {
  id: number;
  title: string;
  servings: number;
  raw_ingredients: string[];
  extraction_status: string;
  variant_group: string;
  ingredients: ReviewIngredient[];
  unverified_count: number;
  /** When a person last checked the whole card, not just its amounts. */
  reviewed_at: string | null;
};

export type ReviewSummary = {
  recipes: number;
  reviewed_recipes: number;
  ingredient_rows: number;
  verified_rows: number;
  unverified_rows: number;
  canonical_ingredients: number;
  priced_ingredients: number;
  human_verdicts: number;
};

export type ReviewFilters = {
  search?: string;
  pending?: boolean;
  unmatched?: boolean;
  page?: number;
};

function toQuery(filters: ReviewFilters): string {
  const params = new URLSearchParams();
  if (filters.search) params.set('search', filters.search);
  if (filters.pending) params.set('pending', 'true');
  if (filters.unmatched) params.set('unmatched', 'true');
  if (filters.page && filters.page > 1) params.set('page', String(filters.page));
  const query = params.toString();
  return query ? `?${query}` : '';
}

export function useReviewSummary() {
  return useQuery({
    queryKey: ['review-summary'],
    queryFn: () => api<ReviewSummary>('/recipes/review-summary/'),
  });
}

export function useReviewRecipes(filters: ReviewFilters) {
  return useQuery({
    queryKey: ['review', filters],
    queryFn: () => api<Paginated<ReviewRecipe>>(`/recipes/review/${toQuery(filters)}`),
  });
}

type IngredientEdit = {
  id: number;
  total_quantity?: string | null;
  unit?: string;
  ingredient_name?: string;
  optional?: boolean;
  verified?: boolean;
};

export function useCorrectIngredient() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: IngredientEdit) =>
      api<ReviewIngredient>(`/recipes/review/ingredients/${id}/`, {
        method: 'PATCH',
        body,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['review'] });
      queryClient.invalidateQueries({ queryKey: ['review-summary'] });
    },
  });
}

export function useCorrectMatch() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, accepted }: { id: number; accepted: boolean }) =>
      api<{ removed: boolean }>(`/recipes/review/matches/${id}/`, {
        method: 'POST',
        body: { accepted },
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['review'] });
      queryClient.invalidateQueries({ queryKey: ['review-summary'] });
      // Which product is chosen is what the list says to buy, and what it
      // costs — corrections now come from the list itself.
      queryClient.invalidateQueries({ queryKey: ['shopping-list'] });
      queryClient.invalidateQueries({ queryKey: ['plan-costs'] });
    },
  });
}

export function useMarkReviewed() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reviewed }: { id: number; reviewed: boolean }) =>
      api<{ id: number; reviewed_at: string | null }>(
        `/recipes/review/recipes/${id}/reviewed/`,
        { method: 'POST', body: { reviewed } },
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['review'] });
      queryClient.invalidateQueries({ queryKey: ['review-summary'] });
    },
  });
}

/** How a premade blend could be made or replaced, where that is known.
 *
 * Null for ordinary food, and for a blend nobody has described yet.
 */
export type Substitution = {
  /** Shop ingredients it can be assembled from. */
  components: string[];
  /** Things sold in shops that would do instead. */
  substitutes: string[];
  /** Where the amounts came from: 'blueapron' | 'blog' | 'equal' | 'llm'. */
  amounts_source: string;
};

export type MatchQueueRow = {
  id: number;
  name: string;
  aliases: string[];
  parent: string;
  /** How many recipes call for it — how much one answer here is worth. */
  uses: number;
  matches: ReviewMatch[];
  substitution: Substitution | null;
};

export type MatchQueueFilters = {
  search?: string;
  unmatched?: boolean;
  unconfirmed?: boolean;
  page?: number;
  /** A match belongs to a (store, ingredient) pair, so the queue is per shop. */
  store?: number | null;
  /** Only blends with a description, or only everything else. */
  substitution?: 'yes' | 'no' | null;
};

/** `enabled` is false when the filters ask for nothing, so the queue is not
 *  fetched unfiltered on the way to showing an empty list. */
export function useMatchQueue(filters: MatchQueueFilters, enabled = true) {
  const params = new URLSearchParams();
  if (filters.search) params.set('search', filters.search);
  if (filters.unmatched) params.set('unmatched', 'true');
  if (filters.unconfirmed) params.set('unconfirmed', 'true');
  if (filters.store != null) params.set('store', String(filters.store));
  if (filters.substitution) params.set('substitution', filters.substitution);
  if (filters.page && filters.page > 1) params.set('page', String(filters.page));
  const query = params.toString();

  return useQuery({
    queryKey: ['review', 'queue', filters],
    enabled,
    queryFn: () =>
      api<Paginated<MatchQueueRow>>(`/recipes/review/queue/${query ? `?${query}` : ''}`),
  });
}

/** The products matched to one ingredient at one shop.
 *
 * The queue's row shape, asked for by name rather than by what still needs
 * answering — the shopping list already knows which line it is asking about.
 * Off until the panel is opened: the list is the screen used in the aisle,
 * where the wifi is worst, and a request per line would be paid on every
 * visit for a question rarely asked.
 */
export function useIngredientMatches(
  ingredientId: number,
  storeId: number | null,
  enabled: boolean,
) {
  const params = new URLSearchParams({ ingredient: String(ingredientId) });
  if (storeId != null) params.set('store', String(storeId));

  return useQuery({
    queryKey: ['review', 'queue', 'ingredient', ingredientId, storeId],
    enabled,
    queryFn: async () => {
      const page = await api<Paginated<MatchQueueRow>>(
        `/recipes/review/queue/?${params}`,
      );
      return page.results[0] ?? null;
    },
  });
}

export function useEditIngredient() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      ...body
    }: {
      id: number;
      aliases?: string[];
      parent?: string | number | null;
    }) => api<MatchQueueRow>(`/recipes/review/canonical/${id}/`, { method: 'PATCH', body }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['review'] });
      queryClient.invalidateQueries({ queryKey: ['store-gaps'] });
    },
  });
}

export function useMergeIngredient() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, loser }: { id: number; loser: number }) =>
      api<{ merged: number }>(`/recipes/review/canonical/${id}/merge/`, {
        method: 'POST',
        body: { loser },
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['review'] });
      queryClient.invalidateQueries({ queryKey: ['review-summary'] });
      queryClient.invalidateQueries({ queryKey: ['store-gaps'] });
      queryClient.invalidateQueries({ queryKey: ['coverage'] });
    },
  });
}

export type MergeCandidate = {
  id: number;
  name: string;
  category: string;
  recipes: number;
  products: number;
  aliases: string[];
};

/** Ingredients that might be the same food as this one, by name or alias.
 *
 * Unused rows are included, unlike the "never cook with" picker: a duplicate
 * no recipe references is exactly the one worth folding away, so filtering it
 * out would hide the best candidates.
 */
export function useMergeSearch(term: string, excludeId: number) {
  const params = new URLSearchParams({
    search: term,
    include_unused: 'true',
    exclude: String(excludeId),
  });
  return useQuery({
    queryKey: ['ingredient-search', 'merge', term, excludeId],
    queryFn: () => api<MergeCandidate[]>(`/recipes/ingredients/?${params}`),
    enabled: term.trim().length >= 2,
  });
}
